import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, ShoppingCart, Clock, DollarSign, Plus, Pencil, Trash2, X, LogOut, Settings, Box, MessageSquare } from 'lucide-react';
import { getProducts, getCategories, createProduct, updateProduct, deleteProduct, getOrders, updateOrderStatus, deleteOrder, getStats, getSettings, updateSettings, getContacts, deleteContact } from '../lib/api';
import { formatPrice, LOGO } from '../data/assets';
import { useToast } from '../hooks/use-toast';

const empty = { name: '', category: '', price: '', short_description: '', description: '', features: '', images: '', featured: false, in_stock: true };

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [cats, setCats] = useState([]);
  const [settings, setSettingsState] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const token = localStorage.getItem('nl_admin_token');

  const loadAll = () => {
    getStats().then(setStats).catch(() => navigate('/admin'));
    getProducts().then(setProducts).catch(() => {});
    getOrders().then(setOrders).catch(() => {});
    getContacts().then(setContacts).catch(() => {});
    getCategories().then(setCats).catch(() => {});
    getSettings().then(setSettingsState).catch(() => {});
  };

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    loadAll();
    // eslint-disable-next-line
  }, []);

  const logout = () => { localStorage.removeItem('nl_admin_token'); navigate('/admin'); };

  const openNew = () => { setEditing(null); setForm({ ...empty, category: cats[0]?.name || '' }); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, price: String(p.price), features: (p.features || []).join('\n'), images: (p.images || []).join('\n') });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, category: form.category, price: parseFloat(form.price) || 0,
      short_description: form.short_description, description: form.description,
      features: form.features.split('\n').map(s => s.trim()).filter(Boolean),
      images: form.images.split('\n').map(s => s.trim()).filter(Boolean),
      featured: !!form.featured, in_stock: !!form.in_stock,
    };
    try {
      if (editing) { await updateProduct(editing.id, payload); toast({ title: 'Product updated' }); }
      else { await createProduct(payload); toast({ title: 'Product added' }); }
      setShowForm(false); loadAll();
    } catch (err) { toast({ title: 'Error saving product', variant: 'destructive' }); }
  };

  const removeProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await deleteProduct(id); toast({ title: 'Product deleted' }); loadAll();
  };

  const changeStatus = async (id, status) => { await updateOrderStatus(id, status); loadAll(); };
  const removeOrder = async (id) => { if (!window.confirm('Delete this order?')) return; await deleteOrder(id); loadAll(); };
  const removeContact = async (id) => { if (!window.confirm('Delete this message?')) return; await deleteContact(id); loadAll(); };

  const saveSettings = async (e) => {
    e.preventDefault();
    const payload = { whatsapp_number: settings.whatsapp_number, store_name: settings.store_name, email: settings.email, phone: settings.phone, address: settings.address };
    if (settings.new_password) payload.admin_password = settings.new_password;
    try { await updateSettings(payload); toast({ title: 'Settings saved' }); if (payload.admin_password) { localStorage.setItem('nl_admin_token', payload.admin_password); } }
    catch { toast({ title: 'Error saving settings', variant: 'destructive' }); }
  };

  const statCards = [
    { icon: Package, label: 'Products', value: stats.products || 0, color: '#0f7a37' },
    { icon: ShoppingCart, label: 'Total Orders', value: stats.orders || 0, color: '#f5a623' },
    { icon: Clock, label: 'Pending', value: stats.pending || 0, color: '#e5352b' },
    { icon: DollarSign, label: 'Revenue', value: formatPrice(stats.revenue || 0), color: '#0d5228' },
    { icon: MessageSquare, label: 'Messages', value: stats.contacts || 0, color: '#37b34a' },
  ];

  const statusColors = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-blue-100 text-blue-700', delivered: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700' };

  return (
    <div className="min-h-screen bg-[#f4faf5]">
      {/* topbar */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="logo" className="h-9" />
            <span className="font-head font-bold text-[#0d5228] hidden sm:block">Admin Dashboard</span>
          </div>
          <button onClick={logout} className="nl-btn text-sm border border-gray-200 hover:bg-gray-50 px-4 py-2"><LogOut size={16} /> Logout</button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[['overview', 'Overview', Box], ['products', 'Products', Package], ['orders', 'Orders', ShoppingCart], ['messages', 'Messages', MessageSquare], ['settings', 'Settings', Settings]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`nl-btn px-4 py-2 text-sm ${tab === k ? 'bg-[#0f7a37] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#0f7a37]'}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {/* overview */}
        {tab === 'overview' && (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {statCards.map((s, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ background: `${s.color}18` }}>
                    <s.icon size={22} style={{ color: s.color }} />
                  </div>
                  <div className="text-2xl font-extrabold text-[#14181b]">{s.value}</div>
                  <div className="text-sm text-gray-500">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-head font-bold text-lg mb-4">Recent Orders</h3>
              {orders.slice(0, 5).map(o => (
                <div key={o.id} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                  <div>
                    <div className="font-semibold text-sm">{o.order_number} — {o.customer_name}</div>
                    <div className="text-xs text-gray-400">{o.items.length} item(s) • {formatPrice(o.total)}</div>
                  </div>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusColors[o.status] || ''}`}>{o.status}</span>
                </div>
              ))}
              {orders.length === 0 && <p className="text-sm text-gray-400 py-4">No orders yet.</p>}
            </div>
          </div>
        )}

        {/* products */}
        {tab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-head text-xl font-bold">Products ({products.length})</h2>
              <button onClick={openNew} className="nl-btn bg-[#0f7a37] hover:bg-[#0d5228] text-white px-4 py-2 text-sm"><Plus size={16} /> Add Product</button>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f4faf5] text-left text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Product</th>
                      <th className="px-4 py-3 font-semibold hidden md:table-cell">Category</th>
                      <th className="px-4 py-3 font-semibold">Price</th>
                      <th className="px-4 py-3 font-semibold hidden sm:table-cell">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(p => (
                      <tr key={p.id} className="border-t border-gray-50 hover:bg-[#f9fdf9]">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img src={p.images?.[0]} alt="" className="w-10 h-10 object-contain bg-[#f6faf7] rounded-lg p-1" />
                            <span className="font-medium line-clamp-1 max-w-[220px]">{p.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{p.category}</td>
                        <td className="px-4 py-3 font-semibold">{formatPrice(p.price)}</td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          {p.featured && <span className="text-xs bg-[#fff3dd] text-[#b47500] px-2 py-0.5 rounded-full mr-1">Featured</span>}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${p.in_stock ? 'bg-[#eaf6ec] text-[#0f7a37]' : 'bg-red-50 text-red-500'}`}>{p.in_stock ? 'In Stock' : 'Out'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-blue-50 text-blue-500"><Pencil size={15} /></button>
                            <button onClick={() => removeProduct(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* orders */}
        {tab === 'orders' && (
          <div>
            <h2 className="font-head text-xl font-bold mb-4">Orders ({orders.length})</h2>
            <div className="space-y-4">
              {orders.map(o => (
                <div key={o.id} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-head font-bold text-[#0d5228]">{o.order_number}</div>
                      <div className="text-sm text-gray-600 mt-1">{o.customer_name} • {o.phone || 'No phone'}</div>
                      {o.address && <div className="text-xs text-gray-400">{o.address}</div>}
                      <div className="text-xs text-gray-400 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={o.status} onChange={e => changeStatus(o.id, e.target.value)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg border-0 outline-none ${statusColors[o.status] || ''}`}>
                        {['pending', 'confirmed', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => removeOrder(o.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-50">
                    {o.items.map((it, k) => (
                      <div key={k} className="flex justify-between text-sm py-1">
                        <span className="text-gray-600">{it.name} x{it.quantity}</span>
                        <span className="font-medium">{formatPrice(it.price * it.quantity)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold text-[#0d5228] pt-2">
                      <span>Total</span><span>{formatPrice(o.total)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {orders.length === 0 && <p className="text-gray-400 text-center py-10">No orders yet.</p>}
            </div>
          </div>
        )}

        {/* messages */}
        {tab === 'messages' && (
          <div>
            <h2 className="font-head text-xl font-bold mb-4">Contact Messages ({contacts.length})</h2>
            <div className="space-y-4">
              {contacts.map(m => (
                <div key={m.id} className="bg-white rounded-2xl border border-gray-100 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-head font-bold text-[#0d5228]">{m.name}</div>
                      <div className="text-sm text-gray-600 mt-0.5">{m.phone || 'No phone'} {m.email ? `• ${m.email}` : ''}</div>
                      <div className="text-xs text-gray-400 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                    </div>
                    <button onClick={() => removeContact(m.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                  </div>
                  <p className="mt-3 pt-3 border-t border-gray-50 text-sm text-gray-700 leading-relaxed">{m.message}</p>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-gray-400 text-center py-10">No messages yet.</p>}
            </div>
          </div>
        )}

        {/* settings */}
        {tab === 'settings' && (
          <div className="max-w-xl">
            <h2 className="font-head text-xl font-bold mb-4">Store Settings</h2>
            <div className="bg-[#eaf6ec] border border-[#37b34a]/30 rounded-2xl p-4 mb-4 flex items-start gap-3">
              <MessageSquare size={20} className="text-[#0f7a37] mt-0.5" />
              <p className="text-sm text-[#0d5228]">Change the <strong>WhatsApp Number</strong> below to instantly update the floating WhatsApp button and all "Order on WhatsApp" buttons across the store. Use full international format without <em>+</em> or spaces, e.g. <span className="font-mono">923001234567</span>.</p>
            </div>
            <form onSubmit={saveSettings} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
              {[['store_name', 'Store Name'], ['whatsapp_number', 'WhatsApp Number (e.g. 923001234567)'], ['phone', 'Contact Phone'], ['email', 'Email'], ['address', 'Address']].map(([k, label]) => (
                <div key={k}>
                  <label className="text-sm font-medium text-gray-600">{label}</label>
                  <input value={settings[k] || ''} onChange={e => setSettingsState({ ...settings, [k]: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1" />
                </div>
              ))}
              <div>
                <label className="text-sm font-medium text-gray-600">New Admin Password (leave blank to keep current)</label>
                <input type="password" value={settings.new_password || ''} onChange={e => setSettingsState({ ...settings, new_password: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1" />
              </div>
              <button type="submit" className="nl-btn bg-[#0f7a37] hover:bg-[#0d5228] text-white px-5 py-2.5">Save Settings</button>
            </form>
          </div>
        )}
      </div>

      {/* product form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="font-head font-bold text-lg">{editing ? 'Edit Product' : 'Add Product'}</h3>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Product Name *</label>
                <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Category *</label>
                  <select required value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1">
                    <option value="">Select</option>
                    {cats.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Price (Rs) *</label>
                  <input required type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Short Description</label>
                <input value={form.short_description} onChange={e => setForm({ ...form, short_description: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Full Description</label>
                <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Features (one per line)</label>
                <textarea rows={3} value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Image URLs (one per line)</label>
                <textarea rows={2} value={form.images} onChange={e => setForm({ ...form, images: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37] mt-1" />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_stock} onChange={e => setForm({ ...form, in_stock: e.target.checked })} /> In Stock</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="nl-btn bg-[#0f7a37] hover:bg-[#0d5228] text-white px-6 py-2.5 flex-1">{editing ? 'Update' : 'Add'} Product</button>
                <button type="button" onClick={() => setShowForm(false)} className="nl-btn border border-gray-200 px-6 py-2.5">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
